function _toConsumableArray(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

import _ from 'underscore';
import MapUtil from 'gw-portals-util-js/MapUtil';
import ViewModelUtil from 'gw-portals-viewmodel-js/ViewModelUtil';
/** Module providing compilation facilities for the specific set of "platform definitions" (functions, operators,
 * etc...).
 *
 * Supported types/aliases:
 *   * expressionEvaluator == (args : Object[]) => Object - function taking arguments as
 *     an array and returning expression value.
 *   * kindCompiler == (expr : ExpressionDTO, ctx : CompileContext) => expressionEvaluator - compiler for the
 *     specific expression kind.
 */

/** NO-VALUE value (like null/unknown in SQL). */
var NONSENSE = Object.freeze({});

function cleanse(v) {
    return ViewModelUtil.isViewModelNode(v) ? v.aspects.typedValue : v;
}

/**
 * Applies an eager function to its arguments.
 * @param {Function} fun function to apply.
 * @param {Array} funArgExprs expression defining arguments to function.
 * @param {Array} exprArgs arguments to the expression itself.
 * @returns {*}
 */
function applyEagerFunction(fun, funArgExprs, exprArgs) {
    var funArgs = funArgExprs.map(function (funArgExpr) {
        /*
         * function which expects values and not expressions.
         */
        return cleanse(funArgExpr(exprArgs));
    });
    if (funArgs.includes(NONSENSE)) {
        return NONSENSE;
    }
    return fun.apply(undefined, _toConsumableArray(funArgs));
}

/** Compiles a constant expression into expression evaluator.
 * @param {{ConstantExpressionDTO}} expr constant expression definition.
 * @returns {{expressionEvaluator}} constant expression evaluator.
 */
var compileConst = function () {
    /** Mapping from constant type into type factory. */
    var valueFactoriesForType = {
        'int': _.identity,
        'long': _.identity,
        'string': _.identity,
        'BigDecimal': _.identity,
        'boolean': _.identity,
        'dto': _.identity,
        'null': _.constant(null),
        'Date': function (_Date) {
            function Date(_x) {
                return _Date.apply(this, arguments);
            }

            Date.toString = function () {
                return _Date.toString();
            };

            return Date;
        }(function (str) {
            return new Date(str);
        })
    };

    return function (expr, buildContext) {
        var valueFactory = MapUtil.get(valueFactoriesForType, expr.type);
        if (valueFactory) {
            return _.constant(valueFactory(expr.value));
        }

        if (expr.type.substring(0, 5) === 'enum ') {
            return _.constant(expr.value);
        }

        if (expr.type.substring(0, 8) === 'typekey ') {
            return _.constant(buildContext.typekeyLookup(expr.type.substring(8), expr.value));
        }

        throw new Error('Unsupported constant type ' + expr.type);
    };
}();

/**
 * Compiles a parameter access expression.
 * @param {{ParameterExpressionDTO}} expr expression to compile.
 * @returns {{expressionEvaluator}} parameter value evaluator.
 */
function compileParameter(expr) {
    var paramIdx = expr.index;
    return function (args) {
        return args[paramIdx];
    };
}

/** Compiles a property access expression. */
var compileFieldAccess = function () {
    function _decapitalize(str) {
        return str.charAt(0).toLowerCase() + str.substring(1);
    }

    /* Fetches a property from the object. */
    function getProperty(obj, propName) {
        if (obj === NONSENSE || _.isUndefined(obj) || _.isNull(obj)) {
            return obj;
        }

        /*
         * Keep navigating VM nodes as much as possible.
         */
        if (ViewModelUtil.isViewModelNode(obj)) {
            var propValue = obj[propName];
            if (!propValue) {
                propValue = obj[_decapitalize(propName)];
            }

            if (propValue) {
                return propValue;
            }

            obj = obj.aspects.typedValue;
        }

        var r = obj[propName];
        if (r) {
            return r;
        }
        r = obj[_decapitalize(propName)];
        return r;
    }

    return function (expr, buildContext) {
        var baseEvaluator = buildContext.compile(expr.base);
        var propName = expr.propertyName;

        return function (args) {
            return getProperty(baseEvaluator(args), propName);
        };
    };
}();

/** Method invocation compiler. */
var compileInvocation = function () {
    return function (expr, buildContext) {
        var ns = expr.container;
        var funcName = expr.name;
        var argExprs = expr.params.map(buildContext.compile);

        var maybeOp = buildContext.operators.getFunction(ns, funcName);
        if (maybeOp) {
            return _.partial(maybeOp, argExprs);
        }

        var maybeFunction = buildContext.functions.getFunction(ns, funcName);
        if (maybeFunction) {
            return _.partial(applyEagerFunction, maybeFunction, argExprs);
        }
        throw new Error('Could not find function ' + funcName + ' in ' + ns);
    };
}();

/**
 * Compiles a "translate" expression.
 * @param {Object} expr
 * @param {Object} buildContext
 * @returns {Function}
 */
function compileTranslate(expr, buildContext) {
    var args = expr.arguments.map(buildContext.compile);
    args.unshift(_.constant(expr.displayKey));
    return _.partial(applyEagerFunction, buildContext.translate, args);
}

/** Mapping from kind name to expression compiler for that kind. */
var kindCompilers = {
    'const': compileConst,
    'parameter': compileParameter,
    'getfield': compileFieldAccess,
    'nativecall': compileInvocation,
    'translate': compileTranslate
};

/* Creates base (non-caching) compiler. */
function createBaseCompiler(operators, functions, translate, typekeyLookup) {
    function compileExpr(expr) {
        var kindCompiler = MapUtil.get(kindCompilers, expr.kind);
        if (!kindCompiler) {
            throw new Error('Unsupported expression kind ' + expr.kind);
        }
        return kindCompiler(expr, ctx);
    }

    var ctx = {
        'compile': compileExpr,
        operators: operators,
        functions: functions,
        translate: translate,
        typekeyLookup: typekeyLookup
    };

    return {
        compile: function compile(expr) {
            var compiledFn = compileExpr(expr);
            return function () {
                /**
                 * the parameter. We have to return a value, not the node.
                 */
                return cleanse(compiledFn(Array.prototype.slice.call(arguments)));
            };
        }
    };
}

export default {
    /** Creates a compiler. This compiler caches result of operations based on the expression
     * <em>identity</em>.
     * @param {*} operators operators namespace.
     * @param {*} functions function namespace.
     * @param {Function} translate translation function.
     * @param {Function} typekeyLookup function used to lookup typekeys (for typekey constant). It takes typekey name and
     * typecode and returns a typecode object.
     * @returns {Object} compiler.
     */
    'createCompiler': function createCompiler(operators, functions, translate, typekeyLookup) {
        return createBaseCompiler(operators, functions, translate, typekeyLookup);
    },
    NONSENSE: NONSENSE,
    cleanse: cleanse
};


//////////////////
// WEBPACK FOOTER
// ./common/js/src/edge/api/gw-portals-exp-lang-js/_ExpressionCompiler.js
// module id = 886
// module chunks = 0